// PythonQuiz.jsx
import React, { useEffect, useMemo, useState } from "react";
import questionsAll from "./data/python-quiz.json"; // <- ensure this file exists
import tvLogo from "../../images/techventurellc-logo.png";

/* -------------------- helpers -------------------- */
const shuffle = (arr) => {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};
const mmss = (s) => {
  const m = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${m}:${ss}`;
};
const esc = (t = "") => t.replace(/&/g, "&amp;");

/** Read a same-origin image as dataURL so it embeds in the SVG download. */
function toDataURL(url) {
  return fetch(url)
    .then((r) => r.blob())
    .then(
      (b) =>
        new Promise((res) => {
          const reader = new FileReader();
          reader.onload = () => res(reader.result);
          reader.readAsDataURL(b);
        })
    )
    .catch(() => null);
}

/* ------------- certificate (SVG) with logo & frame ------------- */
async function downloadCertificateSVG({
  name,
  email,
  score,
  total,
  title = "Python Proficiency Assessment",
  logoUrl = tvLogo,
}) {
  const percent = Math.round((score / total) * 100);
  const now = new Date().toLocaleDateString();
  const dataLogo = await toDataURL(logoUrl); // may be null if not found

  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="1600" height="1000">
  <defs>
    <linearGradient id="bg" x1="0" x2="1" y1="0" y2="1">
      <stop offset="0%" stop-color="#fff7ed"/>
      <stop offset="100%" stop-color="#fff1f2"/>
    </linearGradient>
    <linearGradient id="accent" x1="0" x2="1" y1="0" y2="1">
      <stop offset="0%" stop-color="#2563eb"/>
      <stop offset="100%" stop-color="#06b6d4"/>
    </linearGradient>
    <filter id="cardShadow" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="8" stdDeviation="12" flood-color="#000" flood-opacity="0.2"/>
    </filter>
    <style>
      .title { font: 900 80px 'Segoe UI', Roboto, Ubuntu, system-ui; fill:#1f2937 }
      .subtitle { font: 700 40px 'Segoe UI', Roboto, Ubuntu, system-ui; fill:#374151; letter-spacing: 3px }
      .label { font: 500 28px 'Segoe UI', Roboto, Ubuntu, system-ui; fill:#4b5563 }
      .value { font: 600 40px 'Segoe UI', Roboto, Ubuntu, system-ui; fill:#111827 }
      .small { font: 500 20px 'Segoe UI', Roboto, Ubuntu, system-ui; fill:#6b7280 }
    </style>
  </defs>

  <!-- decorative border -->
  <rect x="30" y="30" width="1540" height="940" rx="24" fill="none" stroke="url(#accent)" stroke-width="10"/>
  <rect x="60" y="60" width="1480" height="880" rx="20" fill="none" stroke="#bae6fd" stroke-width="6"/>

  <!-- certificate body card -->
  <rect x="120" y="140" width="1360" height="720" rx="28" fill="url(#bg)" filter="url(#cardShadow)"/>

  <!-- top ribbon -->
  <path d="M120 220 H1480" stroke="url(#accent)" stroke-width="18" stroke-linecap="round"/>
  <path d="M120 260 H1480" stroke="#93c5fd" stroke-width="6" stroke-linecap="round"/>

  <!-- logo (centered) -->
  ${dataLogo ? `<image href="${dataLogo}" x="730" y="160" width="140" height="140" preserveAspectRatio="xMidYMid meet"/>` : ``}

  <!-- headings -->
  <text x="800" y="360" text-anchor="middle" class="subtitle">CERTIFICATE OF ACHIEVEMENT</text>
  <text x="800" y="440" text-anchor="middle" class="title">${esc(title)}</text>

  <!-- awarded to -->
  <text x="800" y="520" text-anchor="middle" class="label">AWARDED TO</text>
  <text x="800" y="585" text-anchor="middle" class="value">${esc(name || "Tech Venture Learner")}</text>
  ${email ? `<text x="800" y="625" text-anchor="middle" class="small">${esc(email)}</text>` : ""}

  <!-- result row -->
  <g transform="translate(290, 680)">
    <rect width="1020" height="110" rx="16" fill="#fff" stroke="#e5e7eb" stroke-width="3"/>
    <text x="40" y="72" class="label">Score</text>
    <text x="130" y="78" class="value">${percent}%</text>

    <text x="250" y="72" class="label">Date</text>
    <text x="320" y="78" class="value">${esc(now)}</text>

    <text x="570" y="72" class="label">Issuer</text>
    <text x="660" y="78" class="value">Tech Venture LLC</text>
  </g>

  <!-- seal -->
  <g transform="translate(1290, 260)">
    <circle cx="0" cy="0" r="70" fill="url(#accent)"/>
    <circle cx="0" cy="0" r="58" fill="#fff" opacity="0.92"/>
    <text x="0" y="10" text-anchor="middle" class="subtitle" fill="#2563eb" font-size="36">TV</text>
  </g>

  <!-- footer note -->
  <text x="800" y="880" text-anchor="middle" class="small">
    Issued By https://techventurellc.com/badges • Congratulations on your achievement!
  </text>
</svg>`;

  const blob = new Blob([svg], { type: "image/svg+xml" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `techventure-python-certificate.svg`;
  a.click();
  URL.revokeObjectURL(url);
}

/* -------------------- component -------------------- */
export default function PythonQuiz() {
  // config: 20 questions → 10 minutes
  const TOTAL_QUESTIONS = 20;
  const TOTAL_SECONDS = 10 * 60;

  // stages: "intro" | "active" | "result"
  const [stage, setStage] = useState("intro");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [touched, setTouched] = useState({ name: false, email: false });

  const [secondsLeft, setSecondsLeft] = useState(TOTAL_SECONDS);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [endedByTimer, setEndedByTimer] = useState(false);

  // pick 20 questions per attempt
  const questions = useMemo(() => shuffle(questionsAll).slice(0, TOTAL_QUESTIONS), []);

  // timer
  useEffect(() => {
    if (stage !== "active") return;
    if (secondsLeft <= 0) {
      setEndedByTimer(true);
      setStage("result");
      return;
    }
    const t = setInterval(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [stage, secondsLeft]);

  // scroll top on question change
  useEffect(() => {
    if (stage === "active") window.scrollTo({ top: 0, behavior: "smooth" });
  }, [current, stage]);

  // score
  const score = useMemo(
    () =>
      questions.reduce((acc, q) => {
        const sel = answers[q.id];
        return acc + (typeof sel === "number" && sel === q.answer ? 1 : 0);
      }, 0),
    [answers, questions]
  );

  // validation
  const nameInvalid = touched.name && !name.trim();
  const emailInvalid = touched.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  // actions
  const startQuiz = () => {
    setTouched({ name: true, email: true });
    if (!name.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return;
    setStage("active");
    setSecondsLeft(TOTAL_SECONDS);
    setCurrent(0);
    setAnswers({});
    setEndedByTimer(false);
  };
  const selectOption = (qid, idx) => setAnswers((a) => ({ ...a, [qid]: idx }));
  const next = () => setCurrent((i) => Math.min(i + 1, questions.length - 1));
  const prev = () => setCurrent((i) => Math.max(i - 1, 0));
  const submit = () => setStage("result");

  const currentQ = questions[current];
  const answeredCount = Object.keys(answers).length;

  const shareUrl =
    typeof window !== "undefined"
      ? `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
          `${window.location.origin}/proficiencyTest?badge=python&score=${score}&total=${TOTAL_QUESTIONS}`
        )}`
      : "#";

  return (
    <div className="container-lg py-5 quiz-minh">
      {/* Title + timer */}
      <div className="row justify-content-center">
        <div className="col-12 col-lg-8 col-xl-7">
          <div className="d-flex align-items-start justify-content-between flex-wrap gap-2 mb-3">
            <div>
              <h1 className="h2 mb-1">Python Proficiency Test</h1>
              <div className="text-muted">20 questions • 10 minutes • One question at a time</div>
            </div>
            {stage === "active" && (
              <div className="text-end" aria-live="polite" title="Time remaining">
                <span className="badge bg-white fs-5 px-3 py-2">⏱ {mmss(secondsLeft)}</span>
                <div className="text-muted small mt-1">
                  {answeredCount}/{TOTAL_QUESTIONS} answered
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* INTRO */}
      {stage === "intro" && (
        <div className="row justify-content-center">
          <div className="col-12 col-lg-8 col-xl-7">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h2 className="h5 mb-2">Ready to begin?</h2>
                <p className="text-muted">
                  You’ll have <strong>10 minutes</strong> to answer <strong>20</strong> randomly selected Python questions.
                  You can navigate <em>Previous</em> / <em>Next</em> before submitting.
                </p>

                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <label className="form-label fw-semibold">Your name (required — used on the certificate)</label>
                    <input
                      className={`form-control ${nameInvalid ? "is-invalid" : ""}`}
                      maxLength={60}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                      placeholder="Enter your full name"
                      required
                    />
                    {nameInvalid && <div className="invalid-feedback">Please enter your name.</div>}
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label fw-semibold">Email (required)</label>
                    <input
                      type="email"
                      className={`form-control ${emailInvalid ? "is-invalid" : ""}`}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                      placeholder="you@example.com"
                      required
                    />
                    {emailInvalid && <div className="invalid-feedback">Enter a valid email address.</div>}
                  </div>
                </div>

                <div className="d-flex gap-2 mt-3">
                  <button className="btn btn-primary" onClick={startQuiz} disabled={!name.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())}>
                    Yes, I’m ready
                  </button>
                  <a href="/proficiencyTest" className="btn btn-outline-secondary">Not now</a>
                </div>
              </div>
            </div>

            <div className="alert alert-secondary small mt-3">
              The timer starts once you click “Yes, I’m ready”.
            </div>
          </div>
        </div>
      )}

      {/* ACTIVE */}
      {stage === "active" && (
        <div className="row justify-content-center">
          <div className="col-12 col-lg-8 col-xl-7">
            <div className="card shadow-sm border-0 mb-3">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="small text-muted">Question {current + 1} of {TOTAL_QUESTIONS}</div>
                  <div className="progress w-25" style={{ height: 8 }}>
                    <div className="progress-bar" role="progressbar" style={{ width: `${((current + 1) / TOTAL_QUESTIONS) * 100}%` }} />
                  </div>
                </div>

                <h2 className="h5 mb-3">{currentQ.question}</h2>

                <div className="list-group">
                  {currentQ.options.map((opt, idx) => {
                    const selected = answers[currentQ.id] === idx;
                    return (
                      <button
                        key={idx}
                        type="button"
                        className={`list-group-item list-group-item-action ${selected ? "active" : ""}`}
                        onClick={() => selectOption(currentQ.id, idx)}
                        aria-pressed={selected}
                      >
                        {String.fromCharCode(65 + idx)}. {opt}
                      </button>
                    );
                  })}
                </div>

                <div className="d-flex justify-content-between mt-3">
                  <button className="btn btn-outline-secondary" onClick={prev} disabled={current === 0}>← Previous</button>
                  {current < TOTAL_QUESTIONS - 1 ? (
                    <button className="btn btn-primary" onClick={next}>Next →</button>
                  ) : (
                    <button className="btn btn-success" onClick={submit}>Review & Submit</button>
                  )}
                </div>
              </div>
            </div>

            <div className="alert alert-info small mb-5">
              You can change answers anytime before submitting. The timer runs continuously.
            </div>
          </div>
        </div>
      )}

      {/* RESULT */}
      {stage === "result" && (
        <div className="row justify-content-center">
          <div className="col-12 col-lg-9 col-xl-8">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h2 className="h5 mb-0">Your results</h2>
                  <span className={`badge ${score >= 14 ? "bg-success" : "bg-warning"} fs-6`}>{score}/{TOTAL_QUESTIONS}</span>
                </div>
                {endedByTimer && <div className="alert alert-warning py-2 mb-3">Time’s up — your test was auto-submitted.</div>}
                <p className="text-muted">
                  Great work, <strong>{name}</strong>! Review your answers below. Correct answers are highlighted.
                </p>

                <ol className="ps-3">
                  {questions.map((q, i) => {
                    const sel = answers[q.id];
                    const correct = q.answer;
                    return (
                      <li key={q.id} className="mb-2">
                        <div className="fw-semibold">{i + 1}. {q.question}</div>
                        <ul className="list-unstyled mb-1">
                          {q.options.map((opt, idx) => {
                            const chosen = sel === idx;
                            const good = idx === correct;
                            return (
                              <li key={idx} className="small">
                                <span className={`badge me-2 ${good ? "bg-success" : chosen && !good ? "bg-danger" : "bg-light text-dark"}`}>
                                  {String.fromCharCode(65 + idx)}
                                </span>
                                {opt}
                                {chosen && !good && <span className="text-danger ms-2">(your choice)</span>}
                                {good && <span className="text-success ms-2">(correct)</span>}
                              </li>
                            );
                          })}
                        </ul>
                      </li>
                    );
                  })}
                </ol>

                <div className="d-flex flex-wrap gap-2 mt-3">
                  <a className="btn btn-primary" href={shareUrl} target="_blank" rel="noreferrer">Share on LinkedIn</a>
                  <button
                    className="btn btn-outline-success"
                    onClick={() =>
                      downloadCertificateSVG({
                        name: name || "Tech Venture Learner",
                        email: email || "",
                        score,
                        total: TOTAL_QUESTIONS,
                        logoUrl: tvLogo,
                      })
                    }
                  >
                    Download Certificate (SVG)
                  </button>
                  <button className="btn btn-outline-secondary" onClick={() => { setStage("intro"); setTouched({ name: false, email: false }); }}>
                    Try Again
                  </button>
                </div>
              </div>
            </div>

            <div className="alert alert-secondary mt-4 small mb-5">
              Keep practicing: <a href="/python">Python Q&amp;A</a> · <a href="/codingRound">Coding challenges</a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
